import React, {PureComponent} from "react"
import {uniqBy} from "lodash"
import {Row, Col} from "react-bootstrap"

import {User, Team} from "@irati/types"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import MAXIMUM_REWARD_RECIPIENTS from "@irati/reward/constants/maximumRewardRecipients"
import UserSearch from "@irati/ui/src/UserSearch"
import rewardWalletTracking from "@irati-tracking/wallet/rewardWallet"

import {officeForUser} from "@irati/identity/utils/tenant/hasMultipleOffices"
import Validation from "@irati/utils/validation"
import SelectedUser from "./Checkout/Steps/Recipients/SelectedUser"
import {FlexRow} from "./styled"

interface Props extends InjectIntl {
  recipients: User[],
  setRecipients: (recipients: User[]) => void,
  isCredit?: boolean,
  allowMultipleRecipients?: boolean,
  sameBUOnly?: boolean,
  isRequired?: boolean
}

interface State {
  hasTrackingEventFired: boolean,
  businessUnit?: Team
}

class Recipients extends PureComponent<Props, State> {
  static defaultProps = {
    allowMultipleRecipients: true,
    sameBUOnly: true
  }

  state: State = {
    hasTrackingEventFired: false
  }

  componentDidMount() {
    if (this.props.sameBUOnly) {
      officeForUser().then((office) => {
        if (office) {
          this.setState({businessUnit: office})
        }
      })
    }
  }

  handleClick = () => {
    if (this.props.isCredit && !this.state.hasTrackingEventFired) {
      rewardWalletTracking.clickUserDropdown()
      this.setState({hasTrackingEventFired: true})
    }
  }

  addRecipient = (_: any, user) => {
    if (user) {
      const {recipients, setRecipients, isCredit} = this.props
      if (isCredit) {
        rewardWalletTracking.selectUser()
      }
      setRecipients(uniqBy(recipients.concat(user), "uuid"))
    }
  }

  removeUser = (removeUser: User) => {
    const {recipients, setRecipients} = this.props
    setRecipients(recipients.filter((user) => user !== removeUser))
  }

  render() {
    const {recipients, allowMultipleRecipients, formatMessage, isRequired} = this.props
    const {businessUnit} = this.state
    const showSearch = allowMultipleRecipients ? recipients.length < MAXIMUM_REWARD_RECIPIENTS : recipients.length < 1
    const recipientsLimitReached = recipients.length >= MAXIMUM_REWARD_RECIPIENTS

    const teamIds = []
    let extra: React.ReactNode = ""
    if (businessUnit) {
      teamIds.push(businessUnit.uuid)
      extra = <FormattedMessage id="platform.recipients.list.recipientsHintBUOnlyExtra" />
    }

    return (
      <Row>
        <Col md={12}>
          {showSearch && (
            <FlexRow onClick={this.handleClick}>
              <UserSearch
                id="recipients-selector"
                hideLabel
                removeSelf
                onChange={this.addRecipient}
                placeholder="platform.fields.selectRecipient.placeholder"
                controlShouldRenderValue={false}
                recipients={recipients.map(({uuid}) => uuid)}
                teamIds={teamIds}
                validation={isRequired &&
                Validation.string(formatMessage({id: "platform.defaultValidation.required"}))
                  .test(
                    "Recipient list cannot be empty",
                    "platform.defaultValidation.required",
                    () => recipients.length)}
                hint={
                  allowMultipleRecipients &&
                  !recipientsLimitReached && (
                    <FormattedMessage
                      id={
                        recipients.length > 0 ? "platform.recipients.list.recipientsHint" : "platform.recipients.list.recipientsHintDefault"
                      }
                      values={{count: MAXIMUM_REWARD_RECIPIENTS - recipients.length, extra}} />
                  )
                } />
            </FlexRow>
          )}

          {allowMultipleRecipients && recipientsLimitReached && (
            <h4>
              <FormattedMessage id="platform.recipients.list.maxRecipientsMsg" />
            </h4>
          )}

          {recipients.map((user: User, index) => (
            <SelectedUser key={user.uuid} index={index} removeUser={this.removeUser} user={user} />
          ))}
        </Col>
      </Row>
    )
  }
}

export default injectIntl(Recipients)
